import { Persona } from '../api/Persona';

export type PersonaVariant = {
  baseName: string;
  version: number | null;
};

/**
 * Parses common "versioned persona name" formats:
 * - "Caesar v1", "Caesar V2"
 * - "Caesar (v1)", "Caesar - v2"
 * - "Caesar version 1"
 */
export function parsePersonaVariantName(name?: string): PersonaVariant {
  const raw = (name ?? '').trim();
  if (!raw) return { baseName: '', version: null };

  // e.g. "Caesar v1", "Caesar (v2)", "Caesar - v 3"
  {
    const m = raw.match(/^(.*?)(?:\s+|[-(]\s*)v\s*(\d+)\)?\s*$/i);
    if (m) {
      return {
        baseName: (m[1] ?? '').trim(),
        version: Number(m[2]),
      };
    }
  }

  // e.g. "Caesar version 6"
  {
    const m = raw.match(/^(.*?)\s+version\s+(\d+)\s*$/i);
    if (m) {
      return {
        baseName: (m[1] ?? '').trim(),
        version: Number(m[2]),
      };
    }
  }

  return { baseName: raw, version: null };
}

export function getPersonaBaseName(name?: string): string {
  const { baseName } = parsePersonaVariantName(name);
  return baseName;
}

export type PersonaGroup = {
  /** e.g. "Caesar" */
  baseName: string;
  /** Sorted by version desc (unknown versions last) */
  variants: Persona[];
  /** Variant chosen for display purposes */
  primary: Persona;
};

function versionSortKey(p: Persona): number {
  const v = parsePersonaVariantName(p?.name).version;
  // Unknown version sorts last
  return typeof v === 'number' && Number.isFinite(v) ? v : -1;
}

export function groupPersonasByBaseName(personas: Persona[]): PersonaGroup[] {
  const buckets = new Map<string, { baseName: string; variants: Persona[] }>();

  for (const persona of personas) {
    const { baseName } = parsePersonaVariantName(persona?.name);
    const safeBase = (baseName || persona?.name || '').trim();
    const key = safeBase.toLowerCase();
    if (!buckets.has(key)) buckets.set(key, { baseName: safeBase, variants: [] });
    buckets.get(key)!.variants.push(persona);
  }

  // Avoid `for..of` over Map iterators to stay compatible with TS configs
  // that don't enable `--downlevelIteration`.
  const groups: PersonaGroup[] = Array.from(buckets.values()).map((bucket) => {
    const variants = [...bucket.variants].sort((a, b) => versionSortKey(b) - versionSortKey(a));
    const primary = variants[0]!;
    return {
      baseName: bucket.baseName,
      variants,
      primary,
    };
  });

  // Stable-ish ordering: alphabetical by display base name
  groups.sort((a, b) => a.baseName.localeCompare(b.baseName));
  return groups;
}

